import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";
import { Tabs, Tab, Box } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Grid, Card } from "@mui/material";
import PropTypes from "prop-types";
import MDTypography from "components/MDTypography";
import TooltipNoInbois from "./TooltipNoInbois";
import MDBox from "components/MDBox";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "../examples/Tables/DataTable";
import { apiSyncAndGetNotis, apiGetTindakanNotis, apiPostNotis, apiHapusNotis } from "api/notis";
import { useMaterialUIController, triggerUuuPendingRefresh } from "context";
import CustomPagination from "components/CustomPagination";
import NotisSearch from "components/STSearch/NotisSearch";
import TambahTerimaNotis from "components/STPopup/TambahTerimaNotis";
import { printBorangPenyerahan } from "components/STPrint/PrintBorangPenyerahan";
import printNotisPreview from "components/STPrint/PrintNotis";
import { tabStyles } from "utils/tabStyles";

// Columns will be created inside the component to access selection state

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB");
}

function NotisTunggakan() {
  const { showSuccess, showError, showWarning } = useSnackbar();
  const { showConfirm } = useConfirmDialog();
  const [controller, dispatch] = useMaterialUIController();
  const location = useLocation();
  const navigate = useNavigate();
  // Handler for LIHAT button in Senarai Tindakan
  const handleLihatTindakan = async () => {
    if (tabIndex !== 1) return;
    if (selectedTindakan.size !== 1) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih satu baris untuk lihat fail PDF.' });
      return;
    }
    const selectedKey = Array.from(selectedTindakan)[0];
    const row = tindakanRows.find(r => r.key === selectedKey);
    if (!row || !row.sample || !row.sample.bukti) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Tiada fail PDF untuk baris ini.' });
      return;
    }
    let bukti = row.sample.bukti;
    if (bukti && typeof bukti === 'object') {
      if (bukti.type === 'Buffer' && Array.isArray(bukti.data)) {
        bukti = String.fromCharCode(...bukti.data);
      } else {
        bukti = bukti.filename || bukti.name || bukti.fileName || '';
      }
    }
    // Build preferred URL (no_borang) with CRA proxy-friendly fetch
    const url = row.sample.no_borang
      ? `/api/notis/file-by-borang/${encodeURIComponent(row.sample.no_borang)}`
      : `/api/notis/file/${encodeURIComponent(bukti)}`;
    try {
      const resp = await fetch(url, { headers: { Accept: 'application/pdf' } });
      if (!resp.ok) {
        throw new Error(`Gagal memuat PDF (status ${resp.status})`);
      }
      const blob = await resp.blob();
      // Basic validation: ensure it's a PDF blob
      const mime = blob.type || 'application/pdf';
      const pdfBlob = mime === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
      // Revoke later to free memory
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (e) {
      window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: e.message || 'Gagal membuka fail PDF.' });
    }
  };
  // State for Senarai Tindakan tab
  const [tindakanRows, setTindakanRows] = useState([]);
  const [tindakanLoading, setTindakanLoading] = useState(false);
  const [tindakanError, setTindakanError] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Always refresh respective tab data on switch
    if (newValue === 1) {
      fetchTindakanRows();
    } else if (newValue === 0) {
      loadData();
    }
  };

  const handleHapusTindakan = async () => {
    if (tabIndex !== 1) return;
    if (selectedTindakan.size !== 1) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih satu baris untuk HAPUS.' });
      return;
    }
    // Use the first selected row's no_borang as the reference
    const selectedKey = Array.from(selectedTindakan)[0];
    const row = tindakanRows.find(r => r.key === selectedKey);
    const noBorang = row?.sample?.no_borang || row?.no_borang;
    if (!noBorang) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Tiada no_borang ditemui pada baris terpilih.' });
      return;
    }
    const confirmed = await showConfirm({
      title: 'Hapus Notis',
      message: `Hapus semua rekod notis untuk no_borang ${noBorang}?`,
      confirmText: 'Hapus',
      type: 'error'
    });
    if (!confirmed) return;
    try {
      await apiHapusNotis(noBorang);
      // Refresh tindakan list
      setSelectedTindakan(new Set());
      await fetchTindakanRows();
  window.appNotify && window.appNotify({ color: 'success', icon: 'check_circle', title: 'Berjaya', content: 'Notis berjaya dihapus.' });
    } catch (e) {
  window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: e.message || 'Ralat ketika menghapus notis' });
    }
  };

  const handlePostTindakan = async () => {
    if (tabIndex !== 1) return;
    if (selectedTindakan.size === 0) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih sekurang-kurangnya satu baris untuk POST.' });
      return;
    }
    // If exactly one row is selected, validate and show a specific error immediately
    if (selectedTindakan.size === 1) {
      const key = Array.from(selectedTindakan)[0];
      const row = tindakanRows.find(r => r.key === key);
      if (row) {
        // If already posted (mark_notis = 2 / Makluman UUU), show notice
        const alreadyUpdated = (row?.sample?.makluman_uuu === true) || (row?.sample?.mark_notis === 2) || (row?.mark_notis === 2);
        if (alreadyUpdated) {
          window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Inbois telah dikemaskini.' });
          return;
        }
        let days = row?.sample?.tempoh_notis_hari ?? row?.tempoh_notis_hari ?? '-';
        const t = row?.sample?.tarikh_notis || row?.tarikh_notis || null;
        if (typeof days !== 'number' && t) {
          const d = new Date(t);
          if (!Number.isNaN(d.getTime())) {
            const today = new Date();
            today.setHours(0,0,0,0);
            d.setHours(0,0,0,0);
            const diffMs = today.getTime() - d.getTime();
            const calc = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            days = calc < 0 ? 0 : calc;
          }
        }
        if (!(typeof days === 'number' && days >= 14)) {
          window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Belum capai tempoh notis (>=14 hari)' });
          return;
        }
      }
    }
    try {
      // Collect only allowed rows: tempoh_notis >= 14
      const selectedKeys = Array.from(selectedTindakan);
      const allowedInbois = [];
      let skipped = 0;
      for (const key of selectedKeys) {
        const row = tindakanRows.find(r => r.key === key);
        if (!row) { skipped++; continue; }
        // determine days: prefer backend sample.tempoh_notis_hari
        let days = row?.sample?.tempoh_notis_hari ?? row?.tempoh_notis_hari ?? '-';
        const t = row?.sample?.tarikh_notis || row?.tarikh_notis || null;
        if (typeof days !== 'number' && t) {
          const d = new Date(t);
          if (!Number.isNaN(d.getTime())) {
            const today = new Date();
            today.setHours(0,0,0,0);
            d.setHours(0,0,0,0);
            const diffMs = today.getTime() - d.getTime();
            const calc = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            days = calc < 0 ? 0 : calc;
          }
        }
        if (typeof days === 'number' && days >= 14) {
          if (Array.isArray(row.inbois_list)) allowedInbois.push(...row.inbois_list.map(String));
        } else {
          skipped++;
        }
      }
      const uniqueList = Array.from(new Set(allowedInbois));
      if (!uniqueList.length) {
        // Provide specific error reason based on the first selected row
        const firstKey = selectedKeys[0];
        const firstRow = tindakanRows.find(r => r.key === firstKey);
        const alreadyUpdated = (firstRow?.sample?.makluman_uuu === true) || (firstRow?.sample?.mark_notis === 2) || (firstRow?.mark_notis === 2);
        // compute days for the row similar to table logic
        let days = firstRow?.sample?.tempoh_notis_hari ?? firstRow?.tempoh_notis_hari ?? '-';
        const t = firstRow?.sample?.tarikh_notis || firstRow?.tarikh_notis || null;
        if (typeof days !== 'number' && t) {
          const d = new Date(t);
          if (!Number.isNaN(d.getTime())) {
            const today = new Date();
            today.setHours(0,0,0,0);
            d.setHours(0,0,0,0);
            const diffMs = today.getTime() - d.getTime();
            const calc = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            days = calc < 0 ? 0 : calc;
          }
        }
        const content = alreadyUpdated
          ? 'Inbois telah dikemaskini.'
          : (typeof days === 'number' && days < 14)
            ? 'Belum capai tempoh notis (>=14 hari)'
            : 'Tiada rekod yang memenuhi syarat tempoh (>=14 hari).';
        window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content });
        return;
      }
      const resp = await apiPostNotis(uniqueList);
      // Refresh tindakan list and clear selection
      setSelectedTindakan(new Set());
      await fetchTindakanRows();
      const updated = resp.updated ?? uniqueList.length;
      const msg = skipped > 0
        ? `POST berjaya. Dikemaskini: ${updated} inbois. (${skipped} baris tidak memenuhi syarat dan diabaikan)`
        : `POST berjaya. Dikemaskini: ${updated} inbois.`;
  window.appNotify && window.appNotify({ color: 'success', icon: 'check_circle', title: 'Berjaya', content: msg });
  // Trigger global refresh of UUU pending counts via context
  try { triggerUuuPendingRefresh(dispatch); } catch {}
    } catch (e) {
      window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: e.message || 'Gagal melakukan POST.' });
    }
  };

  const fetchTindakanRows = async () => {
    setTindakanLoading(true);
    setTindakanError("");
    try {
      const res = await apiGetTindakanNotis();
      // Single-stage tindakan: backend returns res.rows only
      const normalize = (rows) =>
        (rows || []).map((r) => ({
          nama: r.nama || "-",
          zon: r.zon || "-",
          no_akaun: r.no_akaun || r.noakaun || "-",
          no_inbois: r.no_inbois || "-",
          tarikh_inbois: formatDate(r.tarikh_inbois),
          tarikh_notis: r.tarikh_notis || null,
          tempoh_notis_hari: (typeof r.tempoh_notis_hari === 'number' ? r.tempoh_notis_hari : null),
          makluman_uuu: r.makluman_uuu === true,
          bil_hari_lewat_bayar:
            typeof r.bil_hari_lewat_bayar === "number"
              ? r.bil_hari_lewat_bayar
              : parseInt(r.bil_hari_lewat_bayar || 0, 10),
          alamat1_harta: r.alamat1_harta || "",
          alamat2_harta: r.alamat2_harta || "",
          poskod_harta: r.poskod_harta || "",
          bandar_harta: r.bandar_harta || "",
          negeri_harta: r.negeri_harta || "",
          stage: 'pertama',
          stage_label: 'Pertama',
          mark_notis: r.mark_notis,
          no_borang: r.no_borang,
          bukti: r.bukti,
        }));
      const merged = normalize(res.rows);
      // Include all rows where mark_notis != 0 (double check, backend already filters)
      const filtered = merged.filter(row => (row.mark_notis ?? 0) !== 0);
      // Group by composite key (nama + no_akaun + zon)
      const groups = new Map();
      for (const r of filtered) {
        const nama = (r.nama || "-").trim();
        const noAkaun = (r.no_akaun || "-").toString().trim();
        const zon = r.zon || "-";
        const key = `${nama}||${noAkaun}||${zon}`;
        const rawDate = r.tarikh_inbois;
        const isoDate = (() => {
          const parts = (rawDate || "").split("/");
          if (parts.length === 3) {
            const [dd, mm, yy] = parts;
            const yyyy = yy.length === 2 ? `20${yy}` : yy;
            return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
          }
          const d = new Date(rawDate);
          if (!Number.isNaN(d.getTime())) return d.toISOString();
          return null;
        })();
        const existing = groups.get(key);
        if (!existing) {
          groups.set(key, {
            key,
            nama,
            zon,
            no_akaun: noAkaun,
            latest_tarikh_iso: isoDate,
            latest_tarikh_label: r.tarikh_inbois,
            bil_inbois: 1,
            stage: 'pertama',
            stage_label: 'Pertama',
            sample: r,
            inbois_list: [String(r.no_inbois)],
          });
        } else {
          existing.bil_inbois += 1;
          existing.inbois_list.push(String(r.no_inbois));
          const ex = existing.latest_tarikh_iso ? new Date(existing.latest_tarikh_iso).getTime() : -Infinity;
          const nx = isoDate ? new Date(isoDate).getTime() : -Infinity;
          if (nx > ex) {
            existing.latest_tarikh_iso = isoDate;
            existing.latest_tarikh_label = r.tarikh_inbois;
          }
        }
      }
      setTindakanRows(Array.from(groups.values()));
    } catch (e) {
      setTindakanError(e.message || "Gagal memuat data tindakan notis");
    } finally {
      setTindakanLoading(false);
    }
  };
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupAccounts, setPopupAccounts] = useState([]);
  // Borang Penyerahan popup state (legacy, removed)
  const handleOpenPopup = () => {
    if (tabIndex !== 0) return;
    // Only use selectedNotis for Senarai Notis tab
    const selectedRows = filteredRows.filter((r) => selectedNotis.has(r.key));
    const accounts = selectedRows.map((group) => {
      const groupNoAkaun = group.no_akaun;
      const groupNoInbois = allRows
        .filter((row) => row.no_akaun === groupNoAkaun)
        .map((row) => row.no_inbois);
      return {
        no_akaun: groupNoAkaun,
        nama: group.nama,
        notis: 1,
        no_inbois_list: groupNoInbois,
      };
    });
    setPopupAccounts(accounts);
    setPopupOpen(true);
  };
  // Direct print (no popup): use selected rows and selected zon
  const handlePrintBorang = async () => {
    if (tabIndex !== 0) return;
    const zon = (search?.zon || '').trim();
    if (!zon) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih Zon terlebih dahulu.' });
      return;
    }
    const selectedRows = filteredRows.filter((r) => selectedNotis.has(r.key));
    if (!selectedRows.length) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih baris dalam Senarai Notis.' });
      return;
    }
    const noAkauns = Array.from(new Set(selectedRows.map(r => String(r.no_akaun))));
    try {
      await printBorangPenyerahan(zon, noAkauns);
    } catch (e) {
      window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: e.message || 'Gagal menjana borang penyerahan.' });
    }
  };
  const handleClosePopup = () => {
    setPopupOpen(false);
    setPopupAccounts([]);
  };
  const handleUploadUEU = async () => {
    await loadData();
    handleClosePopup();
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allRows, setAllRows] = useState([]); // unified list with stage (raw per-invoice)
  const [groupedRows, setGroupedRows] = useState([]); // grouped per akaun+stage
  const [search, setSearch] = useState({ zon: "", no_akaun: "" });
  const [currentPage, setCurrentPage] = useState(1);
  // Separate selection state for each tab
  const [selectedNotis, setSelectedNotis] = useState(() => new Set());
  const [selectedTindakan, setSelectedTindakan] = useState(() => new Set());
  const itemsPerPage = 10;

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      // Force recomputation on page load so flags in DB are up-to-date
      const res = await apiSyncAndGetNotis({ useStored: false });
      const normalize = (rows) =>
        (rows || []).map((r) => ({
          nama: r.nama || "-",
          zon: r.zon || "-",
          no_akaun: r.no_akaun || r.noakaun || "-",
          no_inbois: r.no_inbois || "-",
          tarikh_inbois: formatDate(r.tarikh_inbois),
          bil_hari_lewat_bayar:
            typeof r.bil_hari_lewat_bayar === "number"
              ? r.bil_hari_lewat_bayar
              : parseInt(r.bil_hari_lewat_bayar || 0, 10),
          alamat1_harta: r.alamat1_harta || "",
          alamat2_harta: r.alamat2_harta || "",
          poskod_harta: r.poskod_harta || "",
          bandar_harta: r.bandar_harta || "",
          negeri_harta: r.negeri_harta || "",
          nama_syarikat: r.nama_syarikat || "",
          no_ssm: r.no_ssm || "",
          mark_notis: r.mark_notis,
        }));
      // Single-stage: only 'pertama'
      const merged = normalize(res.pertama);
  // Exclude any rows where mark_notis != 0 (hide already uploaded/posted items)
  const filtered = merged.filter(row => (row.mark_notis ?? 0) === 0);
      const seen = new Set();
      const deduped = [];
      for (const row of filtered) {
        const key = (row.no_inbois || "").toString();
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(row);
        }
      }
      setAllRows(deduped);
  // Build grouped rows by composite (nama + no_akaun + zon + stage)
      const groups = new Map();
      for (const r of deduped) {
        const nama = (r.nama || "-").trim();
        const noAkaun = (r.no_akaun || "-").toString().trim();
        const zon = r.zon || "-";
  const key = `${nama}||${noAkaun}||${zon}`;
        const rawDate = r.tarikh_inbois;
        // keep an ISO sortable date alongside formatted
        const isoDate = (() => {
          // try parse back using en-GB parts or assume already ISO-like
          const parts = (rawDate || "").split("/");
          if (parts.length === 3) {
            // en-GB dd/mm/yyyy
            const [dd, mm, yy] = parts;
            const yyyy = yy.length === 2 ? `20${yy}` : yy;
            return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
          }
          const d = new Date(rawDate);
          if (!Number.isNaN(d.getTime())) return d.toISOString();
          return null;
        })();
        const existing = groups.get(key);
        if (!existing) {
          groups.set(key, {
            key,
            nama,
            zon,
            no_akaun: noAkaun,
            latest_tarikh_iso: isoDate,
            latest_tarikh_label: r.tarikh_inbois,
            bil_inbois: 1,
            stage: 'pertama',
            stage_label: 'Pertama',
            // carry any sample invoice info for printing address if needed
            sample: r,
            // track invoices that make up this group for accurate tooltip
            inbois_list: [String(r.no_inbois)],
          });
        } else {
          existing.bil_inbois += 1;
          existing.inbois_list.push(String(r.no_inbois));
          // update latest date if newer
          const ex = existing.latest_tarikh_iso ? new Date(existing.latest_tarikh_iso).getTime() : -Infinity;
          const nx = isoDate ? new Date(isoDate).getTime() : -Infinity;
          if (nx > ex) {
            existing.latest_tarikh_iso = isoDate;
            existing.latest_tarikh_label = r.tarikh_inbois;
          }
        }
      }
      const groupedArray = Array.from(groups.values());
      setGroupedRows(groupedArray);
    } catch (e) {
      setError(e.message || "Gagal memuat data notis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await loadData();
    })();
    return () => {
      alive = false;
    };
  }, []);

  // If redirected with ?tab=tindakan, open the Senarai Tindakan tab and fetch
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = (params.get('tab') || '').toLowerCase();
    if (tab === 'tindakan' && tabIndex !== 1) {
      setTabIndex(1);
      fetchTindakanRows();
      // Clean the query so refresh doesn’t keep forcing
      navigate('/notis-tunggakan', { replace: true });
    }
  }, [location.search]);

  // Inject keyframes for pulse animation used in TEMPOH NOTIS urgent state
  useEffect(() => {
    const styleId = 'pulseSlowKeyframes';
    if (!document.getElementById(styleId)) {
      const el = document.createElement('style');
      el.id = styleId;
      el.innerHTML = `@keyframes pulseSlow { 0% { transform: scale(1); } 50% { transform: scale(1.08); } 100% { transform: scale(1); } }`;
      document.head.appendChild(el);
    }
  }, []);

  const filteredRows = useMemo(() => {
    let rows = groupedRows;
    if (search.zon) rows = rows.filter((r) => (r.zon || "").toLowerCase() === (search.zon || "").toLowerCase());
    if (search.no_akaun) rows = rows.filter((r) => (r.no_akaun || "").toString().toLowerCase().includes((search.no_akaun || "").toString().toLowerCase()));
    // Sort by nama only (single-stage model)
    return [...rows].sort((a, b) => {
      const an = (a.nama || "").trim();
      const bn = (b.nama || "").trim();
      const aEmpty = !an || an === "-";
      const bEmpty = !bn || bn === "-";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return an.localeCompare(bn, "ms", { sensitivity: "base" });
    });
  }, [groupedRows, search]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageRows = filteredRows.slice(pageStart, pageStart + itemsPerPage);

  // Filtering for Senarai Tindakan tab based on NotisSearch
  const filteredTindakanRows = useMemo(() => {
    let rows = tindakanRows;
    if (search.zon) rows = rows.filter((r) => (r.zon || "").toLowerCase() === (search.zon || "").toLowerCase());
    if (search.no_akaun) rows = rows.filter((r) => (r.no_akaun || "").toString().toLowerCase().includes((search.no_akaun || "").toString().toLowerCase()));
    return rows;
  }, [tindakanRows, search]);

  // Selection helpers
  // For grouped selection, use the composite key
  // Selection helpers for each tab
  const displayedRows = tabIndex === 0 ? pageRows : filteredTindakanRows;
  const allPageSelected = useMemo(() => {
    if (tabIndex === 0) {
      return displayedRows.length > 0 && displayedRows.every((r) => selectedNotis.has(r.key));
    } else {
      return displayedRows.length > 0 && displayedRows.every((r) => selectedTindakan.has(r.key));
    }
  }, [displayedRows, selectedNotis, selectedTindakan, tabIndex]);

  const somePageSelected = useMemo(() => {
    if (tabIndex === 0) {
      return displayedRows.some((r) => selectedNotis.has(r.key)) && !allPageSelected;
    } else {
      return displayedRows.some((r) => selectedTindakan.has(r.key)) && !allPageSelected;
    }
  }, [displayedRows, selectedNotis, selectedTindakan, allPageSelected, tabIndex]);

  const toggleRow = (id) => {
    if (tabIndex === 0) {
      setSelectedNotis((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedTindakan((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  const toggleAllOnPage = (checked) => {
    const rows = displayedRows;
    if (tabIndex === 0) {
      setSelectedNotis((prev) => {
        const next = new Set(prev);
        if (checked) rows.forEach((r) => next.add(r.key));
        else rows.forEach((r) => next.delete(r.key));
        return next;
      });
    } else {
      setSelectedTindakan((prev) => {
        const next = new Set(prev);
        if (checked) rows.forEach((r) => next.add(r.key));
        else rows.forEach((r) => next.delete(r.key));
        return next;
      });
    }
  };

  // Small component to render the row checkbox with proper PropTypes
  const SelectCell = ({ row }) => {
    const id = row.original.key;
    const checked = tabIndex === 0 ? selectedNotis.has(id) : selectedTindakan.has(id);
    return (
      <input
        type="checkbox"
        checked={checked}
        onChange={() => toggleRow(id)}
        style={{ cursor: "pointer" }}
      />
    );
  };
  SelectCell.propTypes = {
    row: PropTypes.object.isRequired,
  };

  // Small component to render values with consistent typography and PropTypes
  const ValueCell = ({ value }) => (
    <MDTypography variant="caption" color="text" fontWeight="medium">
      {value ?? "-"}
    </MDTypography>
  );
  ValueCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  // Columns for Senarai Notis (without Tindakan UEU and Makluman UUU)
  const columns = useMemo(() => [
    {
      Header: (
        <input
          type="checkbox"
          checked={allPageSelected}
          ref={(el) => {
            if (el) el.indeterminate = somePageSelected;
          }}
          onChange={(e) => toggleAllOnPage(e.target.checked)}
          style={{ cursor: "pointer" }}
        />
      ),
      accessor: "__select__",
      align: "center",
      headerAlign: "center",
      width: "5%",
      disableSortBy: true,
      Cell: (cell) => <SelectCell row={cell.row} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>Nama</div>,
      accessor: "nama",
      align: "left",
      headerAlign: "center",
      Cell: (cell) => {
        // Use the group's inbois_list so tooltip count matches Bil Inbois exactly
        const row = cell.row.original;
        const noInboisList = Array.isArray(row.inbois_list) ? row.inbois_list : [];
        return (
          <TooltipNoInbois noInboisList={noInboisList}>
            <ValueCell value={cell.value} />
          </TooltipNoInbois>
        );
      },
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>Zon</div>,
      accessor: "zon",
      align: "left",
      headerAlign: "center",
      width: "15%",
      Cell: (cell) => <ValueCell value={cell.value} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>No Akaun</div>,
      accessor: "no_akaun",
      align: "center",
      headerAlign: "center",
      width: "15%",
      Cell: (cell) => <ValueCell value={cell.value} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>Bil Inbois</div>,
      accessor: "bil_inbois",
      align: "center",
      headerAlign: "center",
      width: "15%",
      Cell: (cell) => <ValueCell value={cell.value} />,
    },
  ], [allPageSelected, somePageSelected, selectedNotis, pageRows, tabIndex]);

  // Columns for Senarai Tindakan (Nama, Zon, No Akaun, Bil Inbois, Tindakan UEU, Makluman UUU)
  const tindakanColumns = useMemo(() => [
    {
      Header: (
        <input
          type="checkbox"
          checked={allPageSelected}
          ref={(el) => {
            if (el) el.indeterminate = somePageSelected;
          }}
          onChange={(e) => toggleAllOnPage(e.target.checked)}
          style={{ cursor: "pointer" }}
        />
      ),
      accessor: "__select__",
      align: "center",
      headerAlign: "center",
      width: "5%",
      disableSortBy: true,
      Cell: (cell) => <SelectCell row={cell.row} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>Nama</div>,
      accessor: "nama",
      align: "left",
      headerAlign: "center",
      Cell: (cell) => {
        const row = cell.row.original;
        const noInboisList = Array.isArray(row.inbois_list) ? row.inbois_list : [];
        return (
          <TooltipNoInbois noInboisList={noInboisList}>
            <ValueCell value={cell.value} />
          </TooltipNoInbois>
        );
      },
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>Zon</div>,
      accessor: "zon",
      align: "left",
      headerAlign: "center",
      width: "15%",
      Cell: (cell) => <ValueCell value={cell.value} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>No Akaun</div>,
      accessor: "no_akaun",
      align: "center",
      headerAlign: "center",
      width: "15%",
      Cell: (cell) => <ValueCell value={cell.value} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>TEMPOH NOTIS</div>,
      accessor: "tempoh_notis",
      align: "center",
      headerAlign: "center",
      width: "18%",
      Cell: (cell) => {
        const row = cell.row?.original || {};
        // Prefer backend-computed days if available
        let days = row?.sample?.tempoh_notis_hari ?? row?.tempoh_notis_hari ?? '-';
        const t = row?.sample?.tarikh_notis || row?.tarikh_notis || null;
        // Fallback to client-side calc if backend value missing
        if (t) {
          if (typeof days !== 'number') {
            const d = new Date(t);
            if (!Number.isNaN(d.getTime())) {
              const today = new Date();
              today.setHours(0,0,0,0);
              d.setHours(0,0,0,0);
              const diffMs = today.getTime() - d.getTime();
              const calc = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              days = calc < 0 ? 0 : calc;
            }
          }
        }
        // Single-stage: use black (<=13) and red (>=14)
        const color = (typeof days === 'number') ? (days >= 14 ? '#c1121f' : '#000') : undefined;
        return (
          <div style={{ textAlign: 'center' }}>
            <MDTypography variant="caption" fontWeight="medium" sx={{ color }}>
              {days}
            </MDTypography>
          </div>
        );
      },
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%", whiteSpace: 'nowrap' }}>MAKLUMAN UUU</div>,
      accessor: "makluman_uuu",
      align: "center",
      headerAlign: "center",
      width: "10%",
      Cell: (cell) => {
        const row = cell.row?.original || {};
        const ok = row?.sample?.makluman_uuu ?? row?.makluman_uuu ?? false;
        return (
          <div style={{ textAlign: 'center' }}>
            {ok ? (
              <CheckCircleIcon sx={{ color: '#2e7d32' }} fontSize="small" />
            ) : (
              <CancelIcon sx={{ color: '#c1121f' }} fontSize="small" />
            )}
          </div>
        );
      },
    },
  ], [allRows, selectedTindakan, tabIndex]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (p) => setCurrentPage(p);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  // Preview handler: ensure at least one group selected. We'll pass a representative row
  // per group to printer; PrintNotis will expand invoices per akaun in Lampiran.
  const handlePreview = async () => {
    const selectedRows = filteredRows.filter((r) => selectedNotis.has(r.key));
    if (!selectedRows.length) {
  window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih sekurang-kurangnya satu rekod untuk dicetak.' });
      return;
    }
    // Map grouped rows to minimal shape expected by print: include nama, zon, stage, no_akaun, and a placeholder no_inbois
    const toPrint = selectedRows.map((g) => ({
      nama: g.nama,
      zon: g.zon,
      stage: 'pertama',
      stage_label: 'Pertama',
      no_akaun: g.no_akaun,
      no_inbois: g.sample?.no_inbois || "",
      tarikh_inbois: g.latest_tarikh_label,
      alamat1_harta: g.sample?.alamat1_harta || "",
      alamat2_harta: g.sample?.alamat2_harta || "",
      poskod_harta: g.sample?.poskod_harta || "",
      bandar_harta: g.sample?.bandar_harta || "",
      negeri_harta: g.sample?.negeri_harta || "",
      nama_syarikat: g.sample?.nama_syarikat || "",
      no_ssm: g.sample?.no_ssm || "",
    }));
    await printNotisPreview(toPrint);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.warning}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Notis Tunggakan
                </MDTypography>
              </MDBox>
              <MDBox>
                {/* Carian/NotisSearch always shown, but actions depend on tab */}
                <NotisSearch
                  search={search}
                  onChange={handleSearchChange}
                  onRebuild={tabIndex === 0 ? () => {
                    // Only allow REBUILD in Notis tab; force recompute of notis flags in DB
                    (async () => {
                      try {
                        setLoading(true);
                        const res = await apiSyncAndGetNotis({ useStored: false });
                        // Reuse normalize/dedup logic from loadData
                        const normalize = (rows) =>
                          (rows || []).map((r) => ({
                            nama: r.nama || "-",
                            zon: r.zon || "-",
                            no_akaun: r.no_akaun || r.noakaun || "-",
                            no_inbois: r.no_inbois || "-",
                            tarikh_inbois: formatDate(r.tarikh_inbois),
                            bil_hari_lewat_bayar:
                              typeof r.bil_hari_lewat_bayar === "number"
                                ? r.bil_hari_lewat_bayar
                                : parseInt(r.bil_hari_lewat_bayar || 0, 10),
                            alamat1_harta: r.alamat1_harta || "",
                            alamat2_harta: r.alamat2_harta || "",
                            poskod_harta: r.poskod_harta || "",
                            bandar_harta: r.bandar_harta || "",
                            negeri_harta: r.negeri_harta || "",
                            mark_notis: r.mark_notis,
                          }));
                        const merged = normalize(res.pertama);
                        const filtered = merged.filter(row => (row.mark_notis ?? 0) === 0);
                        const seen = new Set();
                        const deduped = [];
                        for (const row of filtered) {
                          const key = (row.no_inbois || "").toString();
                          if (!seen.has(key)) { seen.add(key); deduped.push(row); }
                        }
                        setAllRows(deduped);
                        // Build grouped rows by composite (nama + no_akaun + zon)
                        const groups = new Map();
                        for (const r of deduped) {
                          const nama = (r.nama || "-").trim();
                          const noAkaun = (r.no_akaun || "-").toString().trim();
                          const zon = r.zon || "-";
                          const key = `${nama}||${noAkaun}||${zon}`;
                          const rawDate = r.tarikh_inbois;
                          const isoDate = (() => {
                            const parts = (rawDate || "").split("/");
                            if (parts.length === 3) {
                              const [dd, mm, yy] = parts;
                              const yyyy = yy.length === 2 ? `20${yy}` : yy;
                              return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
                            }
                            const d = new Date(rawDate);
                            if (!Number.isNaN(d.getTime())) return d.toISOString();
                            return null;
                          })();
                          const existing = groups.get(key);
                          if (!existing) {
                            groups.set(key, {
                              key,
                              nama,
                              zon,
                              no_akaun: noAkaun,
                              latest_tarikh_iso: isoDate,
                              latest_tarikh_label: r.tarikh_inbois,
                              bil_inbois: 1,
                              stage: 'pertama',
                              stage_label: 'Pertama',
                              sample: r,
                              inbois_list: [String(r.no_inbois)],
                            });
                          } else {
                            existing.bil_inbois += 1;
                            existing.inbois_list.push(String(r.no_inbois));
                            const ex = existing.latest_tarikh_iso ? new Date(existing.latest_tarikh_iso).getTime() : -Infinity;
                            const nx = isoDate ? new Date(isoDate).getTime() : -Infinity;
                            if (nx > ex) {
                              existing.latest_tarikh_iso = isoDate;
                              existing.latest_tarikh_label = r.tarikh_inbois;
                            }
                          }
                        }
                        setGroupedRows(Array.from(groups.values()));
                        // Reset pagination and selection for clarity
                        setCurrentPage(1);
                        setSelectedNotis(new Set());
                        setError("");
                      } catch (e) {
                        setError(e?.message || 'Gagal rebuild notis');
                      } finally {
                        setLoading(false);
                      }
                    })();
                  } : undefined}
                  onPreview={tabIndex === 0 ? () => {
                    // Only allow PREVIEW in Notis tab, use selectedNotis
                    handlePreview();
                  } : undefined}
                  onPrintBorang={tabIndex === 0 ? handlePrintBorang : undefined}
                  loading={loading}
                  selectedCount={tabIndex === 0 ? selectedNotis.size : selectedTindakan.size}
                  onTerimaNotis={tabIndex === 0 ? handleOpenPopup : undefined}
                  tabIndex={tabIndex}
                  onLihat={tabIndex === 1 ? handleLihatTindakan : undefined}
                  lihatDisabled={tabIndex === 1 ? selectedTindakan.size !== 1 : undefined}
                  onPost={tabIndex === 1 ? handlePostTindakan : undefined}
                  onHapus={tabIndex === 1 ? handleHapusTindakan : undefined}
                  hapusDisabled={tabIndex === 1 ? selectedTindakan.size !== 1 : undefined}
                />
                <Box sx={{ px: 2, pb: 2 }}>
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    sx={tabStyles.filled}
                  >
                    <Tab label="Senarai Notis" />
                    <Tab label="Senarai Tindakan" />
                  </Tabs>
                </Box>
                {tabIndex === 0 && (
                  <>
                    <DataTable
                      table={{ columns, rows: pageRows }}
                      showTotalEntries={false}
                      isSorted={false}
                      noEndBorder
                      entriesPerPage={false}
                      canSearch={false}
                    />
                    <CustomPagination
                      currentPage={currentPage}
                      totalPages={Math.max(1, totalPages)}
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                      actualItemsOnPage={pageRows.length}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPrevPage={handlePrevPage}
                      variant="contained"
                      color="info"
                    />
                  </>
                )}
                {tabIndex === 1 && (
                  <>
                    {tindakanLoading ? (
                      <MDTypography variant="button" color="text">Memuat data tindakan...</MDTypography>
                    ) : tindakanError ? (
                      <MDTypography variant="button" color="error">{tindakanError}</MDTypography>
                    ) : (
                      <DataTable
                        table={{ columns: tindakanColumns, rows: filteredTindakanRows }}
                        showTotalEntries={false}
                        isSorted={false}
                        noEndBorder
                        entriesPerPage={false}
                        canSearch={false}
                      />
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
  <TambahTerimaNotis open={popupOpen} onClose={handleClosePopup} onUpload={handleUploadUEU} accounts={popupAccounts} />
    </DashboardLayout>
  );
}

export default NotisTunggakan;
